import { memo, useEffect, useMemo, useState } from "react";
import { createIntl, createIntlCache } from "react-intl";
import defaultMessages from "Locale/en/build.json";
import { equals, memoizeWith } from "ramda";

const labelBaseUrl = window?.atob?.("aHR0cHM6Ly9keW5jb25maWcuYXBwLm1hc3RlcmRvY3MuY29tLmF1L2FwaS9yZXN0L2xhYmVsc2J5Y2xpZW50");

const intlCache = createIntlCache();

// todo: move digest to utils
// from https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest
// todo: might not work in IE11
async function digestMessage(message) {
    const msgUint8 = new TextEncoder().encode(message);                           // encode as (utf-8) Uint8Array
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgUint8);           // hash the message
    const hashArray = Array.from(new Uint8Array(hashBuffer));                     // convert buffer to byte array
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, "0")).join(""); // convert bytes to hex string
    return hashHex;
}

export const getIntl = memoizeWith(
    (locale = "en") => locale,
    (locale = "en", messages, overwrites = {}) => {
        const msg = messages || defaultMessages;
        return createIntl({
            defaultLocale: "en",
            key: locale || "en",
            locale: locale || "en",
            messages: msg,
            ...overwrites
        },
        intlCache
        );
    }
);
const fetchLatestLabels = async client => {

    // something like "331855a4f86c05e077fe34d82....";
    const clienthash = await digestMessage(client || window?.location?.host); // origin
    // for security, only the origin hash is exposed, aka only the origin hash can be querried, not the actual origin
    // if we have a origin (=client) it's trivial to convert it,
    // however, if we want to find all origins (=clients) that only works by trying all combos
    // therefore it's secure and doesn't leak client information
    const req = await fetch(`${labelBaseUrl}/${clienthash}`);
    const data = await req.json();
    const [ messages, lang ] = data?.language_setting?.[0]?.labels?.reduce(([ p, l ], {
        translation, label: { messageId }, language
    }) => [ 
        {
            ...p,
            [messageId]: translation
        },
        language  || l
    ], [ {}, null ]) || [ {}, null ];
    return [ messages, lang ];
};
const fromStorrage = key => {
    const value = localStorage?.getItem?.(key) || undefined;
    return value && JSON.parse(value);
};
const toStorrage = (key, val) => {
    return localStorage?.setItem?.(key, JSON.stringify(val));
};
export const useIntlLoader = (lang = "en-AU", client = undefined) => {
    const latestMsgs = fromStorrage("MESSAGES");
    const [ currentMsgs, setCurrentMsgs ] = useState(latestMsgs);
    const [ labelKey, setLabelKey ] = useState(lang);
    useEffect(() => {
        const fetchAndSetLabels = async () => {
            const [ msg, actualLang ] = await fetchLatestLabels(client);
            if (!equals(msg, currentMsgs)) {
                toStorrage("MESSAGES", msg);
                setCurrentMsgs(msg);
                setLabelKey(`${actualLang}${Date.now()}`);
            }
        };
        // this call might return a rejected promise! This is being cought by global error-handling
        fetchAndSetLabels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ lang, client ]);
    const locale = lang.split("-").shift();
    const intlProv = getIntl(labelKey, currentMsgs, {
        key: labelKey,
        locale
    });
    return intlProv;
};
